<template>
  <v-dialog v-model="dialog" :max-width="$vuetify.breakpoint.xsOnly ? '' : '640'" :fullscreen="$vuetify.breakpoint.xsOnly">
    <v-card>
      <v-toolbar dark color="primary" flat>
        <v-toolbar-title>
          {{ $t('quotations.recipients.documents.title') }} {{ request && request.code }}
          <span v-if="partyName" class="subtitle-1">· {{ partyName }}</span>
        </v-toolbar-title>
        <v-spacer />
        <v-btn icon dark :title="$t('close')" @click="dialog = false"><v-icon>close</v-icon></v-btn>
      </v-toolbar>
      <v-card-text v-if="request" class="pt-5">
        <!-- Tarjouspohja: aloitus, erillään varsinaisesta dokumentista -->
        <div class="grey lighten-4 d-flex align-center rounded pa-3 mb-6">
          <v-icon class="mr-3">mdi-file-document-edit-outline</v-icon>
          <div class="flex-grow-1">
            <div class="subtitle-2">{{ $t('quotations.recipients.documents.template') }}</div>
            <div class="caption grey--text text--darken-1">{{ $t('quotations.recipients.documents.template_help') }}</div>
          </div>
          <v-btn outlined color="primary" class="ml-3" :loading="downloading" :disabled="importing" @click="downloadTemplate">
            {{ $t('quotations.recipients.documents.download_word') }}
          </v-btn>
        </div>

        <!-- Tarjousdokumentti: uusin tuotu Word ja siitä tehty PDF; jokainen tuonti on uusi versio -->
        <div class="d-flex align-center mb-2">
          <div class="caption grey--text text-uppercase">{{ $t('quotations.recipients.documents.document') }}</div>
          <v-spacer />
          <v-btn v-if="word && !readonly" small text color="primary" :loading="importing" @click="$refs.fileInput.click()">
            <v-icon small left>mdi-upload</v-icon>
            {{ $t('quotations.recipients.documents.import_new') }}
          </v-btn>
        </div>
        <v-row v-if="word" dense class="mb-4">
          <v-col v-for="f in files" :key="f.key" cols="12" sm="6">
            <v-card outlined class="d-flex align-center pa-3" @click="download(f.file)">
              <v-icon size="36" :color="f.color" class="mr-3">{{ f.icon }}</v-icon>
              <div class="flex-grow-1">
                <div class="subtitle-2">{{ $t(`quotations.recipients.documents.${f.key}`) }}</div>
                <div class="caption grey--text text--darken-1">{{ $t(`quotations.recipients.documents.${f.key}_hint`) }}</div>
              </div>
              <v-icon color="primary">mdi-download</v-icon>
            </v-card>
          </v-col>
        </v-row>
        <div v-else-if="!loading" class="import-area rounded text-center pa-5 mb-6" @dragover.prevent @drop.prevent="onDrop">
          <template v-if="readonly">
            <div class="body-2 grey--text">{{ $t('quotations.recipients.documents.no_document') }}</div>
          </template>
          <template v-else>
            <div class="body-2 mb-3">{{ $t('quotations.recipients.documents.import_help') }}</div>
            <v-btn color="primary" :loading="importing" :disabled="downloading" @click="$refs.fileInput.click()">
              <v-icon left>mdi-upload</v-icon>
              {{ $t('quotations.recipients.documents.import_word') }}
            </v-btn>
          </template>
        </div>
        <input ref="fileInput" type="file" accept=".docx" style="display: none" @change="onFileChosen" />

        <!-- Lähetys (merkittävissä myös ilman tässä tehtyä PDF:ää) -->
        <div class="caption grey--text text-uppercase mb-2">{{ $t('quotations.recipients.documents.sending') }}</div>
        <div class="d-flex align-center">
          <div class="flex-grow-1">
            <div v-if="request.sentDate" class="body-2 success--text">
              <v-icon small color="success">mdi-check</v-icon>
              {{ $t('quotations.recipients.documents.sent_on', { date: $formatDateNoTime(request.sentDate) }) }}
            </div>
            <template v-else>
              <div class="body-2">{{ $t('quotations.recipients.documents.not_sent') }}</div>
              <div class="caption grey--text text--darken-1">{{ $t('quotations.recipients.documents.sent_help') }}</div>
            </template>
          </div>
          <v-btn v-if="!readonly && !request.sentDate" outlined color="primary" class="ml-3" :disabled="busy" @click="$emit('sent')">
            {{ $t('quotations.recipients.documents.mark_sent') }}
          </v-btn>
        </div>
      </v-card-text>
      <v-divider />
      <v-card-actions>
        <v-spacer />
        <v-btn text @click="dialog = false">{{ $t('close') }}</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import downloadjs from 'downloadjs';
import fileApi from '@/api/file';
import quotationApi from '@/api/quotation';

// Tarjousdokumentti yhdelle tarjoukselle: esitäytetty tarjouspohja, tuotu Word ja siitä tehty PDF sekä lähetetyksi merkintä.
// Jokainen tuonti on uusi versio; näytetään uusin Word ja PDF.
export default {
  name: 'QuotationDocumentDialog',
  props: {
    quotationId: { type: Number, default: null },
    // Tarjous (vastaanottaja); annetaan propsina, jotta lähetyksen jälkeen päivittynyt tieto näkyy
    request: { type: Object, default: null },
    partyName: { type: String, default: '' },
    readonly: { type: Boolean, default: false },
    busy: { type: Boolean, default: false },
  },
  data() {
    return {
      dialog: false,
      word: null,
      pdf: null,
      loading: false,
      downloading: false,
      importing: false,
    };
  },
  computed: {
    files() {
      return [
        { key: 'word', file: this.word, icon: 'mdi-file-word-box', color: '#2b579a' },
        { key: 'pdf', file: this.pdf, icon: 'mdi-file-pdf-box', color: '#d32f2f' },
      ].filter(f => f.file);
    },
  },
  methods: {
    open() {
      this.word = null;
      this.pdf = null;
      this.dialog = true;
      this.load();
    },
    async load() {
      this.loading = true;
      try {
        const [words, pdfs] = await Promise.all([
          fileApi.getFiles(this.quotationId, 'offer', `offer_word_${this.request.id}`),
          fileApi.getFiles(this.quotationId, 'offer', `sent_offer_${this.request.id}`),
        ]);
        this.word = words[words.length - 1] || null;
        this.pdf = pdfs[pdfs.length - 1] || null;
      } catch (error) {
        this.$handleApiError(error);
      }
      this.loading = false;
    },
    async downloadTemplate() {
      this.downloading = true;
      try {
        const blob = await quotationApi.downloadWord(this.quotationId, this.request.id);
        downloadjs(blob, `${this.request.code || 'tarjous'}.docx`, blob.type);
      } catch (error) {
        this.$handleApiError(error);
      }
      this.downloading = false;
    },
    onFileChosen(event) {
      const file = event.target.files[0];
      event.target.value = '';
      this.importWord(file);
    },
    onDrop(event) {
      if (!this.readonly) this.importWord(event.dataTransfer.files[0]);
    },
    async importWord(file) {
      if (!file) return;
      this.importing = true;
      try {
        const saved = await quotationApi.importWord(this.quotationId, this.request.id, file);
        this.word = saved.word;
        this.pdf = saved.pdf;
        this.$showSuccessNotification(this.$t('quotations.recipients.documents.imported'));
      } catch (error) {
        this.$handleApiError(error, error && error.message);
      }
      this.importing = false;
    },
    async download(file) {
      try {
        const blob = new Blob([await fileApi.getFile(this, file)], { type: file.contentType });
        downloadjs(blob, file.filename, file.contentType);
      } catch (error) {
        this.$handleApiError(error);
      }
    },
  },
};
</script>

<style scoped>
.import-area {
  border: 1px dashed rgba(0, 0, 0, 0.3);
}
</style>
